import { createDevConsole } from "../utils/devLogger";
/**
 * Local Cache Service
 * Implements the same caching strategy as the server:
 * - Entity types: Permanent cache (never invalidated)
 * - Entity subtypes: Long-term cache (invalidated only on add/delete)
 */

const console = createDevConsole();

const CACHE_PREFIX = "decisionpen_cache_";

// Cache keys
const CACHE_KEYS = {
  ENTITY_SUBTYPES: "entity_subtypes",
};

// Cache versions (for invalidation)
const CACHE_VERSIONS = {
  ENTITY_SUBTYPES: "v1",
};

/**
 * Get cache key with prefix and version
 */
function getCacheKey(key, version) {
  return `${CACHE_PREFIX}${key}_${version}`;
}

/**
 * Get data from localStorage
 */
function getFromLocalStorage(key) {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    return JSON.parse(data);
  } catch (error) {
    console.error(`Error reading from local cache for ${key}:`, error);
    return null;
  }
}

/**
 * Save data to localStorage
 */
function saveToLocalStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error(`Error saving to local cache for ${key}:`, error);
    return false;
  }
}

/**
 * Remove data from localStorage
 */
function removeFromLocalStorage(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from local cache for ${key}:`, error);
    return false;
  }
}

/**
 * Entity Subtypes Cache
 * Long-term cache - invalidated only on add/delete
 */
export const entitySubtypesCache = {
  /**
   * Get entity subtypes from cache
   */
  get() {
    const key = getCacheKey(
      CACHE_KEYS.ENTITY_SUBTYPES,
      CACHE_VERSIONS.ENTITY_SUBTYPES
    );
    const cached = getFromLocalStorage(key);
    if (cached) {
      console.log("✅ Entity subtypes cache HIT");
    }
    return cached;
  },

  /**
   * Save entity subtypes to cache
   */
  set(data) {
    const key = getCacheKey(
      CACHE_KEYS.ENTITY_SUBTYPES,
      CACHE_VERSIONS.ENTITY_SUBTYPES
    );
    const saved = saveToLocalStorage(key, data);
    if (saved) {
      console.log("✅ Entity subtypes cache SET");
    }
    return saved;
  },

  /**
   * Invalidate entity subtypes cache (called when subtype is added/deleted)
   */
  invalidate() {
    const key = getCacheKey(
      CACHE_KEYS.ENTITY_SUBTYPES,
      CACHE_VERSIONS.ENTITY_SUBTYPES
    );
    console.log("🗑️ Invalidating entity subtypes cache");
    return removeFromLocalStorage(key);
  },

  /**
   * Clear and reload entity subtypes cache
   */
  reload(data) {
    this.invalidate();
    return this.set(data);
  },
};

